import React, { useState } from 'react';
import { X, Gift, Download, Loader2, CheckCircle, BookOpen, ClipboardCheck } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeadMagnetModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedGuides, setSelectedGuides] = useState(['erros', 'checklist']);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const { t } = useLanguage();

  const guides = [
    {
      id: 'erros',
      title: t('leadMagnet.guide1Title'),
      description: t('leadMagnet.guide1Description'),
      icon: BookOpen,
      color: '#E8D5D5'
    },
    {
      id: 'checklist',
      title: t('leadMagnet.guide2Title'),
      description: t('leadMagnet.guide2Description'),
      icon: ClipboardCheck,
      color: '#B5C4B1'
    }
  ];

  const toggleGuide = (guideId) => {
    setSelectedGuides(prev => 
      prev.includes(guideId) 
        ? prev.filter(id => id !== guideId)
        : [...prev, guideId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedGuides.length === 0) {
      setError(t('leadMagnet.selectOne'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/lead-magnet/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          guides: selectedGuides
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erro ao enviar');
      }

      setSent(true);
      
      // Auto close after 4 seconds
      setTimeout(() => {
        onClose();
        setSent(false);
        setName('');
        setEmail('');
        setSelectedGuides(['erros', 'checklist']);
      }, 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {sent ? (
          // Success State
          <div className="p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-[#B5C4B1]/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#B5C4B1]" />
            </div>
            <h3 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-3">
              {t('leadMagnet.sent')}
            </h3>
            <p className="text-[#6B4E37]">
              {t('leadMagnet.checkEmail')} <strong>{email}</strong>
            </p>
            <p className="text-sm text-[#8B7D6B] mt-2">
              {t('leadMagnet.helpPlan')}
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="relative bg-gradient-to-r from-[#D4C4B0]/40 to-[#B5C4B1]/30 p-6">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-[#8B7D6B] hover:text-[#6B4E37] transition-colors"
                data-testid="close-lead-magnet"
                aria-label={t('common.close')}
              >
                <X size={20} />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/80 flex items-center justify-center shadow-sm">
                  <Gift size={28} className="text-[#6B4E37]" />
                </div>
                <div>
                  <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2C2C2C]">
                    {t('leadMagnet.title')}
                  </h2>
                  <p className="text-sm text-[#6B4E37]">
                    {t('leadMagnet.subtitle')}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-[#6B4E37] mb-6">
                {t('leadMagnet.thanks')}
              </p>

              {/* Guide Selection */}
              <div className="space-y-3 mb-6">
                {guides.map((guide) => {
                  const IconComponent = guide.icon;
                  const isSelected = selectedGuides.includes(guide.id);
                  
                  return (
                    <button
                      key={guide.id}
                      type="button"
                      onClick={() => toggleGuide(guide.id)}
                      className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-[#C4B5A0] bg-[#FAF9F7]' 
                          : 'border-[#E8E4E0] hover:border-[#D4C4B0]'
                      }`}
                      data-testid={`guide-${guide.id}`}
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: guide.color }}
                      >
                        <IconComponent size={20} className="text-[#6B4E37]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-[#2C2C2C]">{guide.title}</h4>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-[#C4B5A0] bg-[#C4B5A0]' : 'border-[#D4C4B0]'
                          }`}>
                            {isSelected && <CheckCircle size={12} className="text-white" />}
                          </div>
                        </div>
                        <p className="text-sm text-[#8B7D6B] mt-1">{guide.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#6B4E37] mb-1">
                      {t('contact.name')}
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C]"
                      placeholder={t('leadMagnet.yourName')}
                      required
                      data-testid="lead-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#6B4E37] mb-1">
                      {t('contact.email')}
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] text-[#2C2C2C]"
                      placeholder={t('contact.emailPlaceholder')}
                      required
                      data-testid="lead-email"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-red-500 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading || selectedGuides.length === 0}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#C4B5A0] text-white rounded-lg font-medium hover:bg-[#6B4E37] transition-colors disabled:opacity-50"
                  data-testid="lead-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Download size={18} />
                      {t('leadMagnet.receive')}
                    </>
                  )}
                </button>
              </form>

              <p className="text-xs text-[#8B7D6B] text-center mt-4">
                {t('leadMagnet.noSpam')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadMagnetModal;
